import { useNavigate } from 'react-router';
import { Button, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Dialog as KendoDialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

import { columns } from './grid-columns';
import { Employee } from '../../../models/masters/employee';
import { SiTrackPage } from '../../../components/si-track-page';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { usersApiKeyStore } from '../../../api/users-api-key-store';
import { CustomGridCell } from './custom-grid-cell';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useAuthorize } from '../../../hooks/use-authorize';
import { useState } from 'react';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { ApiErrorComponent } from '../../../components/api-error.component';
import { useDeleteData } from '../../../hooks/useDeleteData';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
  disabledDeleteStyle: { color: 'gray', fontSize: '19px', marginLeft: 7, marginRight: 7 }
};

export const EmployeesPage = () => {
  const navigate = useNavigate();
  const [openDeleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [userID, setUserID] = useState<any>();

  const { data: userData, isLoading: isUserLoading, refetch } = useLoadData<ApiResponse<any>>(
    usersApiKeyStore.getUsers()
  );

  const {
    error,
    isError,
    isLoading: fileDeleteLoading,
    mutateAsync: mutateDeleteFile,
  } = useDeleteData(usersApiKeyStore.deleteUser(userID));

  const handleEditEmployee = async (employee: Employee) => {
    navigate(`/masters/employee/edit/${employee.userID}`);
  };

  const handleCreateEmployee = () => {
    navigate('/masters/employee/create');
  };

  const handleDeleteEmployee = async () => {
    await mutateDeleteFile();
    refetch();
    setUserID('');
    handleClosedDeleteDialog();
  };

  const openConfirmDialog = (userID: any) => {
    setUserID(userID);
    setDeleteDialog(true);
  }

  const deleteEmployeeElement = (dataItem: any) => {
    const isWOScheduledForUser: any = dataItem?.dataItem?.isWOScheduledForUser;
    const isDeletePermission = useAuthorize([UserPermissionName?.DELETE_EMPLOYEE]);
    return (
      <>
        <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_EMPLOYEE]}>
          {
            isWOScheduledForUser ?
              <div className="delete-btn-container">
                <Tooltip title={<Typography variant="body1"> You can&apos;t delete; this user has scheduled work orders.</Typography>}>
                  <DeleteIcon style={styling.disabledDeleteStyle} className="btnDelete" />
                </Tooltip>
              </div>
              :
              <div className="delete-btn-container">
                <IconButton style={{ padding: 5 }} aria-label="delete" onClick={() => openConfirmDialog(dataItem?.dataItem?.userID)}>
                  <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                    <DeleteIcon className="btnDelete" />
                  </Tooltip>
                </IconButton>
              </div>
          }

        </GuardedComponent>
        {!isDeletePermission && (
          <Tooltip title={<Typography variant="body1">You do not have delete permission</Typography>}>
            <DeleteIcon style={{ color: 'gray' }} className="btnDelete" />
          </Tooltip>
        )}
      </>
    );
  }

  const handleClosedDeleteDialog = () => {
    setDeleteDialog(false);
  };

  const isDisplayProfilePermission = useAuthorize([UserPermissionName?.DISPLAY_PROFILE]);
  const updatedColumns = columns.map((col) =>
    col.field === 'userName' ? { ...col, navigate: isDisplayProfilePermission } : col
  );

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <SiTrackPage
      title="Employee"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_EMPLOYEE]}>
          <Button
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleCreateEmployee}
          >
            Add New Employee
          </Button>
        </GuardedComponent>
      }
    >
      <Paper elevation={0} style={styling.paperStyle}>
        <SiTrackGrid
          fileTitle='employee'
          dataItemKey="userID"
          columns={updatedColumns}
          dataItems={userData?.data}
          customCell={(field) =>
            CustomGridCell({
              field,
            })
          }
          viewRoute={(emp) => `/employee/profile/${emp.userID}`}
          onEdit={handleEditEmployee}
          CustomComponent={deleteEmployeeElement}
          isBusy={isUserLoading}
          actionPermissions={{
            view: UserPermissionName.DISPLAY_PROFILE,
            edit: UserPermissionName.EDIT_EMPLOYEE,
            export: UserPermissionName.DOWNLOAD_GRID,
          }}
        />
      </Paper>
      {openDeleteDialog && (
        <KendoDialog className="modalpopup" title={'CONFIRM DELETE'} onClose={handleClosedDeleteDialog}>
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>Are you sure you want to permanently delete?</div>
              <LoadingIndicator isLoading={fileDeleteLoading} />
              <ApiErrorComponent isError={isError} errorRespnse={error} />
            </div>
          </div>
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button className="btn btn-sm btn-default btn-lightGrey" onClick={handleClosedDeleteDialog} autoFocus>
                Cancel
              </button>
              <button
                className="btn btn-sm btn-danger btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                  color: isReadOnlyPermission ? 'black' : '#fff'
                }}
                type="button"
                onClick={handleDeleteEmployee}
                disabled={isUserLoading || isReadOnlyPermission}
              >
                Delete
              </button>
            </div>
          </DialogActionsBar>
        </KendoDialog>
      )}
    </SiTrackPage>
  );
};
